<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="card min-height">
      <div class="card-body">
        <div class="px-4 pb-4">
          <h2 class="card-title-desc">Pending Payments</h2>
          <TableLoading v-if="orders.page === 0"/>
          <div v-else>
            <data-table :columns="columns" :rows="orders.objects" :current-page="currentPage" :has-pagination="false" @onSearch="searchFn">
              <template v-slot:actions="slotProps" v-if="checkForPermission(change_order_payment_status_permission)">
                <b-button
                  v-if="checkForPermission(mark_order_as_paid_permission)"
                  v-b-modal.mark-as-paid
                  variant="primary"
                  size="sm"
                  class="py-1 my-1 text-white"
                  @click="selectOrder(slotProps.row.publicId, slotProps.row.orderBalanceDue)"
                >
                  Make Payment
                </b-button>
                <b-button
                  v-if="checkForPermission(write_off_order_permission)"
                  v-b-modal.write-off
                  variant="dark"
                  size="sm"
                  class="py-1 my-1 text-white"
                  @click="selectWriteOff(
                    slotProps.row.invoiceId,
                    slotProps.row.publicId,
                    slotProps.row.customer.name,
                    slotProps.row.orderBalanceDue,
                    slotProps.row.deliveryDate,
                    slotProps.row.paymentDueDate,
                    slotProps.row.status.orderStatusName,
                    slotProps.row.paymentStatus.name,
                  )">
                  Mark as Written Off
                </b-button>
              </template>
              <div class="row mb-2">
                <div class="col-3">
                  <div class="input-group mb-3">
                    <label class="input-group-text bg-primary text-white">Due Status</label>
                    <select class="form-select" @change="dueStatusFilter($event)">
                      <option value="all" selected>All</option>
                      <option :value="1">Due</option>
                      <option :value="2">Not Due</option>
                    </select>
                  </div>
                </div>
              </div>
            </data-table>
            <b-pagination
                v-model="currentPage"
                :total-rows="orders.total"
                :per-page="perPage"
                size="lg"
                align="center"
                class="mt-3"
            />
          </div>
        </div>
      </div>
    </div>
    <b-modal
        id="mark-as-paid"
        centered
        hide-footer
        hide-header
        title-class="font-18">
      <div class="p-2">
        <h3>Make Payment</h3>
        <mark-as-paid-modal :order-balance-due="orderBalanceDue" @markPaid="markPaid"/>
        <b-button class="mt-3 float-end" variant="dark" block @click="$bvModal.hide('mark-as-paid')">Close</b-button>
      </div>
    </b-modal>
    <b-modal
        id="write-off"
        centered
        hide-footer
        hide-header
        title-class="font-18">
      <div class="p-2">
        <h3>Write Off Order #{{ writeOff.invoiceId }}</h3>
        <div class="row mt-4 h5">
          <div class="col-5 mb-4"><b>Customer:</b></div>
          <div class="col-6 mb-4">{{ writeOff.customer }}</div>
          <div class="col-5 mb-4"><b>Invoice #:</b></div>
          <div class="col-6 mb-4">{{ writeOff.invoiceId }}</div>
          <div class="col-5 mb-4"><b>Delivery Date:</b></div>
          <div class="col-6 mb-4"><date-is-today :current_date="writeOff.deliveryDate"/></div>
          <div class="col-5 mb-4"><b>Payment Due Date:</b></div>
          <div class="col-6 mb-4"><date-is-today :current_date="writeOff.paymentDueDate"/></div>
          <div class="col-5 mb-4"><b>Order Status:</b></div>
          <div class="col-6 mb-4"><order-status-component :order_status="writeOff.orderStatus"/></div>
          <div class="col-5 mb-4"><b>Payment Status:</b></div>
          <div class="col-6 mb-4"><payment-status-component :payment_status="writeOff.paymentStatus"/></div>
          <div class="col-5 mb-4"><b>Balance Due:</b></div>
          <div class="col-6 mb-4">Ksh {{ writeOff.balanceDue | formatNumber }}</div>
        </div>
        <div v-if="!writeOff.writtenOff">
          <b-button v-if="writeOffLoading" variant="primary" size="lg" class="py-2 mt-1 text-white" disabled>
            <i class="bx bx-loader bx-spin font-size-16 align-middle me-2"></i>
            Loading
          </b-button>
          <b-button v-else variant="primary" size="lg" class="py-2 mt-1 text-white" @click="markWrittenOff">Write Off</b-button>
        </div>
        <b-button class="mt-3 float-end" variant="dark" block @click="$bvModal.hide('write-off')">Close</b-button>
      </div>
    </b-modal>
  </Layout>
</template>

<script>
import appConfig from "@/app.config.json";
import Layout from "@/router/layouts/main"
import PageHeader from "@/components/page-header";
import moment from "moment";
import DataTable from "@/components/data-table";
import {ALL_CUSTOMER_ORDERS_FINANCE} from "@/gql/orders";
import MarkAsPaidModal from "@/components/finance/mark-as-paid-modal";
import {mapActions} from "vuex";
import {successToast} from "@/components/successToast";
import TableLoading from "@/components/TableLoading";
import {permissions} from "@/router/permissions";
import OrderStatusComponent from "@/components/orders/order-status-component";
import DateIsToday from "@/components/orders/date-is-today";
import PaymentStatusComponent from "@/components/finance/payment-status-component";

export default {
  page: {
    title: "Pending Payments",
    meta: [{ name: "description", content: appConfig.description }]
  },
  components: {
    TableLoading,
    MarkAsPaidModal,
    DataTable, PageHeader, Layout,
    OrderStatusComponent, DateIsToday, PaymentStatusComponent},
  name: "pending-payments",
  data() {
    return {
      title: "Pending Payments",
      items: [
        {
          text: "Dashboard",
          href: "/"
        },
        {
          text: "Pending Payments",
          active: true
        }
      ],
      change_order_payment_status_permission: permissions.change_order_payment_status_permission,
      mark_order_as_paid_permission: permissions.mark_order_as_paid_permission,
      write_off_order_permission: permissions.write_off_order_permission,
      selected_order: '',
      writeOff: {
        invoiceId: '',
        orderId: '',
        customer: '',
        balanceDue: '',
        deliveryDate: '',
        paymentDueDate: '',
        orderStatus: '',
        paymentStatus: '',
        writtenOff: false,
      },
      writeOffLoading: false,
      orderBalanceDue: 0,
      filterValue: '',
      orders: {
        page: 0,
        objects: []
      },
      currentPage: 1,
      perPage: 30,
      searchKeyword: "",
      paymentStatus: [20, 40],
      paymentDue: 0,
      columns: [
        {
          field: 'customer.customerCode',
          label: 'Customer Code'
        },
        {
          field: 'customer.name',
          label: 'Customer Name'
        },
        {
          field: 'invoiceId',
          label: 'Order'
        },
        {
          field: 'createdAt',
          label: 'Order Date'
        },
        {
          field: 'deliveryDate',
          label: 'Delivery Date'
        },
        {
          field: 'paymentDueDate',
          label: 'Payment Due Date',
        },
        {
          field: 'due_status',
          label: 'Due Status',
        },
        {
          field: 'orderStatus',
          label: 'Order Status'
        },
        {
          field: 'paymentStatus',
          label: 'Payment Status'
        },
        {
          field: 'grossOrderTotal',
          label: 'Total'
        },
        {
          field: "orderBalanceDue",
          label: "Balance Due"
        },
        {
          field: 'actions',
          label: 'Actions'
        },
      ]
    }
  },
  computed: {
    statusFilter(){
      let records = this.rows
      if (this.filterValue === 'true'){
        records =  this.rows.filter(record => this.isBefore(record.payment_due_date))
      }else if(this.filterValue === 'false'){
        records =  this.rows.filter(record => this.isAfter(record.payment_due_date))
      }
      return records
    }
  },
  apollo: {
    orders: {
      query: ALL_CUSTOMER_ORDERS_FINANCE,
      variables(){
        return {
          page: this.currentPage,
          perPage: this.perPage,
          searchKeyword: this.searchKeyword,
          orderPayment: this.paymentStatus,
          orderStatus: ["601fdbd6-c437-4758-98a2-e807e847f566", "b3180af8-62c8-44ce-8432-64c02c5e5a56", "37bd4482-06ed-409d-845e-bdc6334c5505", "e6c0554c-aed8-4c39-b431-cbc239c0fa40" ], //delivered, loaded, not loaded, open
          paymentDue: this.paymentDue
        }
      }
    }
  },
  watch: {
    currentPage(){
      document.body.scrollTop = 0
      document.documentElement.scrollTop = 0
    }
  },
  mounted() {
    this.currentPage = parseInt(this.$route.query.page) || 1
  },
  methods: {
    ... mapActions({
      changeOrderPaymentStatus: 'orders/changeOrderPaymentStatus'
    }),
     async markPaid(data){
       const dt = {
         orderId: this.selected_order,
         paymentStatus: data.paymentStatus,
         paymentTypeId: data.paymentType.publicId,
         paymentRef: data.paymentRef,
         amountPaid: data.amountPaid,
         paymentType: data.paymentFull,
         paymentReceivedDate: moment(data.paymentReceivedDate).format('YYYY-MM-DD'),
       }
      await this.changeOrderPaymentStatus(dt).then(resp => {
        if(resp.message){
          successToast(`${resp.message}`)
          this.$apollo.queries.orders.refetch()
        }
      })
    },
    selectOrder(orderId, orderBalanceDue){
      this.selected_order = orderId
      this.orderBalanceDue = parseInt(orderBalanceDue)
    },
    searchFn(data){
      this.searchKeyword = data
    },
    isBefore(date){
      return moment(date).isBefore(moment())
    },
    isAfter(date){
      return moment(date).isAfter(moment())
    },
    changeStatusFilter(event){
      this.filterValue = event.target.value
    },
    dueStatusFilter(event){
      const value = event.target.value
      if(event.target.value === 'all'){
        this.paymentDue = 0
      }else {
        this.paymentDue =   parseInt(value)
      }
    },
    selectWriteOff(invoiceId, orderId, customer, balanceDue, deliveryDate, paymentDueDate, orderStatus, paymentStatus) {
      this.writeOff.invoiceId = invoiceId
      this.writeOff.orderId = orderId
      this.writeOff.customer = customer
      this.writeOff.balanceDue = balanceDue
      this.writeOff.deliveryDate = deliveryDate
      this.writeOff.paymentDueDate = paymentDueDate
      this.writeOff.orderStatus = orderStatus
      this.writeOff.paymentStatus = paymentStatus
      this.writeOff.writtenOff = false
    },
    async markWrittenOff() {
      this.writeOffLoading = true
      this.writeOff.writtenOff = true
      const data= {
        orderId: this.writeOff.orderId,
        paymentStatus: 30,
        paymentTypeId: '',
        paymentRef: '',
        amountPaid: '0',
        paymentType: 'Write-Off',
        paymentReceivedDate: moment().format('YYYY-MM-DD'),
      }
      await this.changeOrderPaymentStatus(data).then(resp => {
        if(resp.message){
          successToast(`${resp.message}`)
          this.writeOffLoading = false
          this.$apollo.queries.orders.refetch()
        }
      })
    }
  }
}
</script>

<style scoped>

</style>
